import { defineStore } from "pinia";
import { createAbortController } from "@coronet/cui";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { AxiosError, type AxiosRequestConfig } from "axios";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import moment from "moment";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import cloneDeep from "lodash/cloneDeep";
import { type WorkspaceListItem } from "@/_store/workspaces-list.module.ts";
import { SubscriptionType, WorkspaceType } from "@/constants/workplaces.ts";
import type { Paged, Pagination } from "@/types.ts";
import {
  type BaseMspWorkspacesModuleState,
  type EditSubscriptionData,
  getMspFilters,
  type GlobalMspWorkspaceListItem,
  type MspWorkspaceDetails,
  type MspWorkspaceDialogItem,
  type ParentWorkspaceSearchPermission,
} from "@/_store/msp/msp.module.ts";
import { StoreModule } from "@/constants/store.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import { generateMspWorkspaceFormData } from "@/_helpers/msp-store-actions.ts";

let abortController: AbortController | undefined;

type GlobalWorkspacesModuleState = BaseMspWorkspacesModuleState<GlobalMspWorkspaceListItem>;

const defaultState: GlobalWorkspacesModuleState = {
  pagination: {
    page: 0,
    pageSize: 25,
  },
  pages: 0,
  total: 0,
  workspaces: [],
  showSkeletonLoader: false,
  createWorkspaceInProgress: false,
  createWorkspaceCompleted: false,
  createWorkspaceFailed: false,
  loading: false,
};

export const useGlobalWorkspacesStore = defineStore(StoreModule.GLOBAL_WORKSPACES, {
  state: () => ({ ...defaultState }),
  actions: {
    resetCreateWorkspaceLoaders() {
      this.createWorkspaceInProgress = false;
      this.createWorkspaceFailed = false;
      this.createWorkspaceCompleted = false;
    },
    setPagination(pagination: Pagination) {
      this.pagination = { ...pagination };
    },
    resetPagination() {
      this.pagination = { ...defaultState.pagination };
    },
    async getMspWorkspaces(
      payload: { showSkeletonLoader?: boolean; triggeredByFilters?: boolean } = {
        showSkeletonLoader: false,
        triggeredByFilters: false,
      }
    ) {
      abortController = createAbortController(abortController);
      this.showSkeletonLoader = payload.showSkeletonLoader ?? false;
      this.loading = true;

      if (payload.triggeredByFilters) {
        this.pagination = { page: 0, pageSize: 25 };
      }
      const filtersStore = useFiltersStore();
      const {
        addons,
        types,
        subscriptionTypes,
        modules,
        bundles,
        search,
        page,
        pageSize,
        nearExpirationOnly,
        userProtectionLimit,
      } = {
        ...getMspFilters(filtersStore.filters[FilterContext.GLOBAL_WORKSPACES]),
        ...this.pagination,
      };
      const request: AxiosRequestConfig = {
        ...api.globalWorkspaces(),
        params: {
          addons: addons.join(","),
          types: types?.join(","),
          subscriptionTypes: subscriptionTypes.join(","),
          nearExpirationOnly,
          modules: modules.join(","),
          bundles: bundles.join(","),
          search,
          page,
          pageSize,
          userProtectionLimit: userProtectionLimit ?? undefined,
        },
      };

      try {
        const { data } = await axiosInstance.request({
          ...request,
          signal: abortController.signal,
        });
        this.workspaces = data.items;
        this.total = data.total;
        this.showSkeletonLoader = false;
        this.loading = false;
      } catch (error) {
        if (error && (error as AxiosError).code === AxiosError.ERR_CANCELED) {
          return;
        }
        console.error(error);
        this.showSkeletonLoader = false;
        this.loading = false;
      }
      abortController = undefined;
    },
    async createWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      const { image, emailImage, ...jsonData } = cloneDeep(payload.item);

      const formData = generateMspWorkspaceFormData(
        jsonData,
        payload.item.image,
        payload.item.emailImage
      );

      const request = {
        ...api.globalWorkspaces(),
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
        method: "POST",
      };

      this.createWorkspaceInProgress = true;
      this.createWorkspaceCompleted = false;
      this.createWorkspaceFailed = false;

      try {
        await axiosInstance.request(request);
        this.createWorkspaceInProgress = false;
        this.createWorkspaceCompleted = true;
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.manageWorkspaces.workspaceUpdated")
        );
      } catch (error) {
        this.createWorkspaceInProgress = false;
        this.createWorkspaceFailed = true;
        console.error(error);
      }
    },
    async editWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      const {
        image,
        showDisabledModules,
        workspaceId,
        scanUnprotectedUsers,
        displayName,
        supportEnabled,
        subscription,
        maxProtectedUsers,
        companyName,
        branding,
        isCoroEmployeeWorkspace,
        emailImage,
        notificationsLevel,
        languageCode,
        labels,
        allowPolicyOverriding,
        mspPolicyId,
        subscriptionRules,
        limited,
        limits,
        netSuiteId,
        netSuiteSubscriptionId,
        netSuiteLegacySubscriptionId,
        isNFR,
        isAcvAccount,
        isRampProgramEnabled,
        rampProgramCommitmentUnits,
      } = payload.item;

      const jsonData = {
        companyName,
        notificationsLevel,
        maxProtectedUsers,
        displayName,
        supportEnabled,
        scanUnprotectedUsers,
        psaEnabled: true, // https://gitlab.com/coronet/bugatti/tickets/roadmap/-/issues/1854
        branding: {
          ...branding,
        },
        subscription,
        showDisabledModules,
        isCoroEmployeeWorkspace,
        languageCode,
        labels,
        allowPolicyOverriding,
        mspPolicyId,
        subscriptionRules,
        limited,
        limits,
        netSuiteId,
        netSuiteSubscriptionId,
        netSuiteLegacySubscriptionId,
        isNFR,
        isAcvAccount,
        isRampProgramEnabled,
        rampProgramCommitmentUnits,
      };

      const formData = generateMspWorkspaceFormData(jsonData, image, emailImage);

      const workspaceStore = useWorkspaceStore();
      const { workspace } = workspaceStore;
      const currentWorkspace = workspace.workspaceId;

      const request = {
        ...api.globalWorkspace(workspaceId),
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
        method: "PUT",
      };

      try {
        const { data } = await axiosInstance.request(request);

        // if we are editing workspace in which we are now, then we should update the current workspace data
        if (currentWorkspace === workspaceId) {
          workspaceStore.setWorkspace({
            mfaRequired: data.mfaRequired,
            scanUnprotectedUsers: data.scanUnprotectedUsers,
            workspaceId: data.workspaceId,
            name: data.name,
            type: data.type,
            branding: data.branding,
            supportEnabled: data.supportEnabled,
            subscription,
            showDisabledModules,
            subscriptionType: data.subscriptionType,
            betaComponents: [],
            domain: data.domain,
            isCoronetWorkspace: data.isCoronetWorkspace,
            trialEndDate: data.trialEndDate ?? 0,
            languageCode: data.languageCode,
            allowPolicyOverriding: data.allowPolicyOverriding,
            mspPolicyShortInfo: data.mspPolicyShortInfo,
          });
        }
        await this.getMspWorkspaces();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.manageWorkspaces.workspaceUpdated")
        );
      } catch (error) {
        console.error(error);
      }
    },
    async deleteWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      const request = {
        ...api.globalWorkspace(payload.item.workspaceId),
        method: "DELETE",
      };

      try {
        await axiosInstance.request(request);
        await this.getMspWorkspaces();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.manageWorkspaces.workspaceDeleted")
        );
      } catch (error) {
        console.error(error);
      }
    },
    async convertWorkspace(
      payload: DialogDataConfig<MspWorkspaceDialogItem & { descendantConversionOption?: string }>
    ) {
      const data = {
        type: payload?.item?.type,
        parentWorkspaceId: payload?.item?.parentWorkspaceId,
        descendantConversionOption: payload?.item?.descendantConversionOption,
      };

      const request = {
        ...api.convertGlobalMspWorkspace(payload.item.workspaceId),
        data,
        method: "PUT",
      };

      try {
        await axiosInstance.request(request);
        await this.getMspWorkspaces();
      } catch (error) {
        console.error(error);
      }
    },
    async unarchiveWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      const request = {
        ...api.unarchiveGlobalMspWorkspace(payload.item.workspaceId),
      };

      try {
        await axiosInstance.request(request);
        await this.getMspWorkspaces();
      } catch (error) {
        console.error(error);
      }
    },
    async archiveWorkspace(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      const request = {
        ...api.archiveGlobalMspWorkspace(payload.item.workspaceId),
      };

      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.manageWorkspaces.workspaceArchived")
        );
        await this.getMspWorkspaces();
      } catch (error) {
        console.error(error);
      }
    },
    async editSubscription(payload: EditSubscriptionData) {
      const [workspaceId] = payload.workspaceIds;
      const request = {
        ...api.editGlobalWorkspaceSubscription(),
        data: {
          workspaceIds: payload.workspaceIds,
          subscriptionDto: {
            modules: payload.modules,
            bundles: payload.bundles,
          },
        },
        params: {
          workspaceId,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);
        await this.getMspWorkspaces();
        useWorkspaceStore().$patch((state) => {
          state.workspace.subscription = {
            modules: payload.modules,
            bundles: payload.bundles,
          };
        });
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            "snackbar.messages.manageWorkspaces.workspacesUpdated",
            { n: data.appliedActions },
            data.appliedActions
          )
        );
      } catch (error) {
        console.error(error);
      }
    },
    async stopSubscription(payload: DialogDataConfig<string[]>) {
      const data = {
        workspaceIds: payload.item,
        subscriptionType: SubscriptionType.FREEZE,
      };
      await this.updateSubscriptionType(data);
    },
    async startSubscription(payload: DialogDataConfig<string[]>) {
      const data = {
        workspaceIds: payload.item,
        subscriptionType: SubscriptionType.SUBSCRIPTION,
      };
      await this.updateSubscriptionType(data);
    },
    async updateSubscriptionType({
      subscriptionType,
      workspaceIds,
    }: {
      workspaceIds: string[];
      subscriptionType: SubscriptionType;
    }) {
      const [workspaceId] = workspaceIds;
      const request = {
        ...api.updateGlobalWorkspaceSubscriptionType(),
        data: {
          workspaceIds,
          subscriptionType,
        },
        params: {
          workspaceId,
        },
      };
      try {
        const { data } = await axiosInstance.request(request);
        await this.getMspWorkspaces();
        const workspaceStore = useWorkspaceStore();
        const { workspace } = workspaceStore;
        if (workspace.workspaceId === workspaceId) {
          await workspaceStore.getCurrentWorkspace();
        }
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            "snackbar.messages.manageWorkspaces.workspacesUpdated",
            { n: data.appliedActions },
            data.appliedActions
          )
        );
      } catch (e) {
        console.error(e);
        await this.getMspWorkspaces();
      }
    },
    async extendTrial(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      const request = {
        ...api.extendGlobalWorkspaceTrial(payload.item.workspaceId),
        data: {
          endDate: moment(payload.item.endDate).endOf("day").utc().valueOf(),
        },
      };

      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.manageWorkspaces.workspaceUpdated")
        );
        await this.getMspWorkspaces();
      } catch (error) {
        console.error(error);
      }
    },
    async startTrial(payload: DialogDataConfig<MspWorkspaceDialogItem>) {
      const request = {
        ...api.startTrialForGlobalMspWorkspace(),
        data: {
          workspaceIds: [payload.item.workspaceId],
        },
      };

      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.manageWorkspaces.workspaceUpdated")
        );
        await this.getMspWorkspaces();
      } catch (error) {
        console.error(error);
      }
    },
    async getWorkspaceCandidates(
      search = "",
      permission: ParentWorkspaceSearchPermission,
      pagination: Pagination
    ): Promise<Paged<{ workspaceName: string; workspaceId: string }>> {
      const request = {
        ...api.workspaceGlobalMspCandidates(),
        params: {
          search,
          permission,
          ...pagination,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);
        return {
          items: data.items.map((v: WorkspaceListItem) => ({ ...v, workspaceName: v.name })),
          total: data.total,
        };
      } catch (error) {
        console.error(error);
        return { items: [], total: 0 };
      }
    },
    async checkInvitedAdminAlreadyMspAdmin(data: {
      parentWorkspaceId: string;
      invitedAdmin: string;
    }): Promise<boolean> {
      const request = {
        ...api.checkInvitedAdminAlreadyMspAdmin(),
        data,
      };

      try {
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async searchMspConversionWorkplaceCandidates(query: {
      name: string;
      type?: WorkspaceType;
      channelWorkspaceIdToExclude?: string;
    }): Promise<Paged<WorkspaceListItem> | undefined> {
      const request = {
        ...api.globalConversionWorkplaceCandidates({ ...query, page: 0, pageSize: 25 }),
      };
      try {
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (e) {
        console.error(e);
      }
    },
    async getMspWorkspaceDetails(workspaceId: string): Promise<MspWorkspaceDetails | undefined> {
      const request = {
        ...api.globalWorkspaceDetails(workspaceId),
      };

      try {
        const { data } = await axiosInstance.request(request);
        return data;
      } catch (error) {
        console.error(error);
      }
    },
  },
});
